/**
 * Business Onboarding Wizard Templates
 *
 * Default templates for business onboarding intake wizard.
 * Collects P0 information from business admins during first login.
 *
 * @see Issue #435 - Business Onboarding Intake Wizard
 */

import type { OnboardingWizardTemplate } from './types';

// =============================================================================
// TEMPLATE IDS
// =============================================================================

export const ONBOARDING_TEMPLATE_IDS = {
  DEFAULT: 'tpl-onboard-default',
  LENDER: 'tpl-onboard-lender',
  BROKER: 'tpl-onboard-broker',
} as const;

// =============================================================================
// DEFAULT BUSINESS ONBOARDING TEMPLATE
// =============================================================================

/**
 * Default business onboarding template with P0 questions.
 * Covers company info, lending capabilities, licensing, and team structure.
 */
export const DEFAULT_ONBOARDING_TEMPLATE: OnboardingWizardTemplate = {
  id: ONBOARDING_TEMPLATE_IDS.DEFAULT,
  name: 'Business Onboarding',
  description: 'Standard onboarding wizard for new businesses',
  category: 'onboarding',
  icon: 'business',
  color: '#6366F1', // Primary purple
  tags: ['onboarding', 'setup', 'business'],
  complexity: 'beginner',
  industryFit: ['lending', 'mortgage', 'fintech'],
  data: {
    version: '1.0.0',
    exitBehavior: 'dashboard',
    completionMessage: 'Your business profile is complete! You can now access all features.',
    adminPrefilEnabled: true,
    steps: [
      // Step 1: Company Information
      {
        id: 'step-company-info',
        order: 0,
        name: 'Company Information',
        description: 'Tell us about your company',
        icon: 'domain',
        category: 'company_info',
        skippable: false,
        fields: [
          {
            id: 'field-company-name',
            name: 'companyName',
            label: 'Company Legal Name',
            type: 'text',
            required: true,
            priority: 'P0',
            category: 'company_info',
            businessFieldMapping: 'name',
            adminPrefilable: true,
            placeholder: 'Enter your company legal name',
            helpText: 'The official registered name of your company',
            width: 12,
          },
          {
            id: 'field-industry',
            name: 'industry',
            label: 'Industry',
            type: 'select',
            required: true,
            priority: 'P0',
            category: 'company_info',
            businessFieldMapping: 'industry',
            adminPrefilable: true,
            options: [
              { value: 'mortgage_lending', label: 'Mortgage Lending' },
              { value: 'private_lending', label: 'Private/Hard Money Lending' },
              { value: 'commercial_lending', label: 'Commercial Lending' },
              { value: 'mortgage_brokerage', label: 'Mortgage Brokerage' },
              { value: 'real_estate', label: 'Real Estate' },
              { value: 'title_escrow', label: 'Title & Escrow' },
              { value: 'other', label: 'Other' },
            ],
            width: 6,
          },
          {
            id: 'field-company-size',
            name: 'companySize',
            label: 'Company Size',
            type: 'select',
            required: true,
            priority: 'P0',
            category: 'company_info',
            businessFieldMapping: 'companySize',
            adminPrefilable: true,
            options: [
              { value: '1-5', label: '1-5 employees' },
              { value: '6-20', label: '6-20 employees' },
              { value: '21-50', label: '21-50 employees' },
              { value: '51-200', label: '51-200 employees' },
              { value: '201-500', label: '201-500 employees' },
              { value: '500+', label: '500+ employees' },
            ],
            width: 6,
          },
          {
            id: 'field-website',
            name: 'website',
            label: 'Company Website',
            type: 'text',
            required: false,
            priority: 'P2',
            category: 'company_info',
            adminPrefilable: true,
            placeholder: 'https://yourcompany.com',
            width: 6,
          },
        ],
      },
      // Step 2: Business Capabilities
      {
        id: 'step-capabilities',
        order: 1,
        name: 'Business Capabilities',
        description: 'What services does your company provide?',
        icon: 'account_balance',
        category: 'lending_capabilities',
        skippable: false,
        fields: [
          {
            id: 'field-can-lend',
            name: 'canLend',
            label: 'Direct Lending',
            type: 'checkbox',
            required: false,
            priority: 'P0',
            category: 'lending_capabilities',
            businessFieldMapping: 'canLend',
            adminPrefilable: true,
            helpText: 'Check if your company funds loans directly',
            width: 12,
          },
          {
            id: 'field-can-broker',
            name: 'canBroker',
            label: 'Loan Brokerage',
            type: 'checkbox',
            required: false,
            priority: 'P0',
            category: 'lending_capabilities',
            businessFieldMapping: 'canBroker',
            adminPrefilable: true,
            helpText: 'Check if your company brokers loans to other lenders',
            width: 12,
          },
          {
            id: 'field-can-receive-referrals',
            name: 'canReceiveReferrals',
            label: 'Accept Referrals',
            type: 'checkbox',
            required: false,
            priority: 'P0',
            category: 'lending_capabilities',
            businessFieldMapping: 'canReceiveReferrals',
            adminPrefilable: true,
            helpText: 'Check if your company accepts referrals from partners',
            width: 12,
          },
          {
            id: 'field-lending-products',
            name: 'lendingProducts',
            label: 'Loan Products',
            type: 'multi-select',
            required: false,
            priority: 'P0',
            category: 'lending_capabilities',
            businessFieldMapping: 'lendingProducts',
            adminPrefilable: true,
            conditionalDisplay: {
              field: 'canLend',
              operator: 'equals',
              value: true,
            },
            options: [
              { value: 'bridge', label: 'Bridge Loans' },
              { value: 'fix_flip', label: 'Fix & Flip' },
              { value: 'ground_up', label: 'Ground-Up Construction' },
              { value: 'dscr', label: 'DSCR Rental' },
              { value: 'commercial', label: 'Commercial' },
              { value: 'multifamily', label: 'Multifamily' },
              { value: 'land', label: 'Land Loans' },
            ],
            helpText: 'Select all loan products your company offers',
            width: 12,
          },
          {
            id: 'field-min-loan',
            name: 'minLoanAmount',
            label: 'Minimum Loan Amount',
            type: 'currency',
            required: false,
            priority: 'P1',
            category: 'lending_capabilities',
            businessFieldMapping: 'minLoanAmount',
            adminPrefilable: true,
            conditionalDisplay: {
              field: 'canLend',
              operator: 'equals',
              value: true,
            },
            placeholder: '$100,000',
            width: 6,
          },
          {
            id: 'field-max-loan',
            name: 'maxLoanAmount',
            label: 'Maximum Loan Amount',
            type: 'currency',
            required: false,
            priority: 'P1',
            category: 'lending_capabilities',
            businessFieldMapping: 'maxLoanAmount',
            adminPrefilable: true,
            conditionalDisplay: {
              field: 'canLend',
              operator: 'equals',
              value: true,
            },
            placeholder: '$10,000,000',
            width: 6,
          },
        ],
      },
      // Step 3: Licensing
      {
        id: 'step-licensing',
        order: 2,
        name: 'Licensing',
        description: 'Provide your company licensing information',
        icon: 'verified_user',
        category: 'licensing',
        skippable: true,
        fields: [
          {
            id: 'field-nmls-company-id',
            name: 'nmlsCompanyId',
            label: 'NMLS Company ID',
            type: 'text',
            required: false,
            priority: 'P0',
            category: 'licensing',
            businessFieldMapping: 'nmlsCompanyId',
            adminPrefilable: true,
            placeholder: 'Enter your NMLS Company ID',
            helpText: "Your company's NMLS registration number",
            width: 6,
          },
          {
            id: 'field-lending-states',
            name: 'lendingStates',
            label: 'Licensed Lending States',
            type: 'multi-select',
            required: false,
            priority: 'P0',
            category: 'licensing',
            businessFieldMapping: 'lendingStates',
            adminPrefilable: true,
            conditionalDisplay: {
              field: 'canLend',
              operator: 'equals',
              value: true,
            },
            options: [
              { value: 'AL', label: 'Alabama' },
              { value: 'AK', label: 'Alaska' },
              { value: 'AZ', label: 'Arizona' },
              { value: 'AR', label: 'Arkansas' },
              { value: 'CA', label: 'California' },
              { value: 'CO', label: 'Colorado' },
              { value: 'CT', label: 'Connecticut' },
              { value: 'DE', label: 'Delaware' },
              { value: 'FL', label: 'Florida' },
              { value: 'GA', label: 'Georgia' },
              { value: 'HI', label: 'Hawaii' },
              { value: 'ID', label: 'Idaho' },
              { value: 'IL', label: 'Illinois' },
              { value: 'IN', label: 'Indiana' },
              { value: 'IA', label: 'Iowa' },
              { value: 'KS', label: 'Kansas' },
              { value: 'KY', label: 'Kentucky' },
              { value: 'LA', label: 'Louisiana' },
              { value: 'ME', label: 'Maine' },
              { value: 'MD', label: 'Maryland' },
              { value: 'MA', label: 'Massachusetts' },
              { value: 'MI', label: 'Michigan' },
              { value: 'MN', label: 'Minnesota' },
              { value: 'MS', label: 'Mississippi' },
              { value: 'MO', label: 'Missouri' },
              { value: 'MT', label: 'Montana' },
              { value: 'NE', label: 'Nebraska' },
              { value: 'NV', label: 'Nevada' },
              { value: 'NH', label: 'New Hampshire' },
              { value: 'NJ', label: 'New Jersey' },
              { value: 'NM', label: 'New Mexico' },
              { value: 'NY', label: 'New York' },
              { value: 'NC', label: 'North Carolina' },
              { value: 'ND', label: 'North Dakota' },
              { value: 'OH', label: 'Ohio' },
              { value: 'OK', label: 'Oklahoma' },
              { value: 'OR', label: 'Oregon' },
              { value: 'PA', label: 'Pennsylvania' },
              { value: 'RI', label: 'Rhode Island' },
              { value: 'SC', label: 'South Carolina' },
              { value: 'SD', label: 'South Dakota' },
              { value: 'TN', label: 'Tennessee' },
              { value: 'TX', label: 'Texas' },
              { value: 'UT', label: 'Utah' },
              { value: 'VT', label: 'Vermont' },
              { value: 'VA', label: 'Virginia' },
              { value: 'WA', label: 'Washington' },
              { value: 'WV', label: 'West Virginia' },
              { value: 'WI', label: 'Wisconsin' },
              { value: 'WY', label: 'Wyoming' },
              { value: 'DC', label: 'District of Columbia' },
            ],
            helpText: 'Select all states where your company is licensed to lend',
            width: 12,
          },
        ],
      },
      // Step 4: Team & Preferences
      {
        id: 'step-team',
        order: 3,
        name: 'Team & Preferences',
        description: 'Tell us about your team and how you work',
        icon: 'groups',
        category: 'team_structure',
        skippable: true,
        fields: [
          {
            id: 'field-expected-users',
            name: 'expectedUsers',
            label: 'Expected Team Size',
            type: 'select',
            required: false,
            priority: 'P1',
            category: 'team_structure',
            adminPrefilable: true,
            options: [
              { value: '1', label: 'Just me' },
              { value: '2-5', label: '2-5 users' },
              { value: '6-10', label: '6-10 users' },
              { value: '11-25', label: '11-25 users' },
              { value: '25+', label: '25+ users' },
            ],
            helpText: 'How many team members will use the platform?',
            width: 6,
          },
          {
            id: 'field-monthly-volume',
            name: 'expectedMonthlyVolume',
            label: 'Expected Monthly Loan Volume',
            type: 'select',
            required: false,
            priority: 'P1',
            category: 'team_structure',
            adminPrefilable: true,
            options: [
              { value: '0-10', label: '0-10 loans/month' },
              { value: '11-25', label: '11-25 loans/month' },
              { value: '26-50', label: '26-50 loans/month' },
              { value: '51-100', label: '51-100 loans/month' },
              { value: '100+', label: '100+ loans/month' },
            ],
            helpText: 'Approximate number of loans processed monthly',
            width: 6,
          },
          {
            id: 'field-timezone',
            name: 'timezone',
            label: 'Primary Timezone',
            type: 'select',
            required: false,
            priority: 'P2',
            category: 'preferences',
            adminPrefilable: true,
            options: [
              { value: 'America/New_York', label: 'Eastern Time (ET)' },
              { value: 'America/Chicago', label: 'Central Time (CT)' },
              { value: 'America/Denver', label: 'Mountain Time (MT)' },
              { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
              { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
              { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)' },
            ],
            width: 6,
          },
          {
            id: 'field-referral-source',
            name: 'referralSource',
            label: 'How did you hear about us?',
            type: 'select',
            required: false,
            priority: 'P2',
            category: 'preferences',
            adminPrefilable: true,
            options: [
              { value: 'search', label: 'Search Engine' },
              { value: 'referral', label: 'Referral from colleague' },
              { value: 'conference', label: 'Conference/Trade Show' },
              { value: 'advertisement', label: 'Advertisement' },
              { value: 'social_media', label: 'Social Media' },
              { value: 'other', label: 'Other' },
            ],
            width: 6,
          },
        ],
      },
    ],
  },
};

// =============================================================================
// TEMPLATE COLLECTION
// =============================================================================

export const ONBOARDING_WIZARD_TEMPLATES: OnboardingWizardTemplate[] = [DEFAULT_ONBOARDING_TEMPLATE];

// =============================================================================
// TEMPLATE UTILITIES
// =============================================================================

/**
 * Get all onboarding wizard templates
 */
export function getAllOnboardingTemplates(): OnboardingWizardTemplate[] {
  return ONBOARDING_WIZARD_TEMPLATES;
}

/**
 * Get onboarding template by ID
 */
export function getOnboardingTemplateById(id: string): OnboardingWizardTemplate | undefined {
  return ONBOARDING_WIZARD_TEMPLATES.find((t) => t.id === id);
}

/**
 * Get the default onboarding template
 */
export function getDefaultOnboardingTemplate(): OnboardingWizardTemplate {
  return DEFAULT_ONBOARDING_TEMPLATE;
}
